import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useDropzone} from "react-dropzone";
import {Modal, Blockquote, Button, Loading, Icon, List, ListItem} from "@wfp/ui";
import {iconUpload} from "@wfp/icons";
import classNames from "classnames";
import {BulkUploadResult} from "../../models/types";
import {bulkUploadSchools} from "../../restapi/school";

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const SchoolBulkUploadModal = (props: Props) => {
    const {t} = useTranslation();
    const [result, setResult] = useState<BulkUploadResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const {
        acceptedFiles,
        getRootProps,
        getInputProps,
        isDragActive,
    } = useDropzone({
        accept: {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls']
        },
        maxFiles: 1,
        onDrop: () => {
            setError(null);
            setResult(null);
        }
    });

    const handleUpload = () => {
        if (acceptedFiles.length === 0) {
            setError(t('please_select_file'));
            return;
        }

        setLoading(true);
        setError(null);

        bulkUploadSchools(acceptedFiles[0])
            .then(res => {
                setResult(res);
                if (res.success) {
                    props.onSuccess();
                }
            })
            .catch(err => {
                setError(err.response?.data?.detail || t('upload_failed'));
            })
            .finally(() => setLoading(false));
    };

    const handleClose = () => {
        setResult(null);
        setError(null);
        props.onClose();
    };

    const downloadTemplate = () => {
        window.open('/templates/school_upload_template.xlsx', '_blank');
    };

    return (
        <Modal
            open={props.open}
            modalHeading={t('upload_schools') as string}
            modalLabel={t('bulk_upload') as string}
            primaryButtonText={result?.success ? t('close') as string : t('upload') as string}
            onRequestSubmit={result?.success ? handleClose : handleUpload}
            onRequestClose={handleClose}
            secondaryButtonText={result?.success ? undefined : t('cancel') as string}
            onSecondarySubmit={result?.success ? undefined : handleClose}
            primaryButtonDisabled={loading || (acceptedFiles.length === 0 && !result)}
        >
            {!result && (
                <>
                    <div className="mb-4">
                        <Button kind="tertiary" onClick={downloadTemplate}>
                            {t('download_template')}
                        </Button>
                    </div>

                    <section className="wfp--dropzone">
                        <div {...getRootProps({
                            isDragActive,
                            className: classNames('wfp--dropzone__input', {
                                'wfp--dropzone__input--drag-active': isDragActive
                            })
                        })}>
                            <input {...getInputProps()} />
                            <Icon className="wfp--dropzone__icon" icon={iconUpload} description="upload icon"/>
                            <div>{t('drop_files_or_click')}</div>
                        </div>
                        {acceptedFiles.length > 0 && (
                            <aside className="wfp--dropzone__file-list">
                                <h4>{t('files')}</h4>
                                <List>
                                    {acceptedFiles.map((file) => (
                                        <ListItem key={file.name}>
                                            {file.name} - {Math.round(file.size / 1000)} kB
                                        </ListItem>
                                    ))}
                                </List>
                            </aside>
                        )}
                    </section>

                    {loading && (
                        <div className="mt-4 flex items-center">
                            <Loading withOverlay={false} />
                            <span className="ml-3">{t('processing')}</span>
                        </div>
                    )}

                    {error && (
                        <Blockquote kind="error" withIcon className="mt-4">
                            {error}
                        </Blockquote>
                    )}
                </>
            )}

            {result && (
                <div className="mt-4">
                    {result.success ? (
                        <Blockquote kind="success" withIcon>
                            <p>{t('upload_success')}</p>
                            <ul className="mt-2">
                                <li>{t('created_count')}: {result.created_count}</li>
                                <li>{t('updated_count')}: {result.updated_count}</li>
                                <li>{t('total_rows')}: {result.total_rows}</li>
                            </ul>
                        </Blockquote>
                    ) : (
                        <div>
                            <Blockquote kind="error" withIcon>
                                {t('upload_failed_with_errors')}
                            </Blockquote>
                            <div className="mt-4 max-h-64 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left p-2">{t('row')}</th>
                                            <th className="text-left p-2">{t('field')}</th>
                                            <th className="text-left p-2">{t('error')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.errors.map((err, idx) => (
                                            <tr key={idx} className="border-b">
                                                <td className="p-2">{err.row}</td>
                                                <td className="p-2">{err.field}</td>
                                                <td className="p-2">{err.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};
